import type { AssetReference } from "./assets";
import type { JsonObject } from "./json";

export const NEXRA_MEDIA_API_VERSION = 1 as const;

/** Catalog-level generation taxonomy. Text keeps its native endpoint protocols. */
export const NEXRA_GENERATION_OPERATIONS = [
  "text.generate",
  "image.generate",
  "video.generate",
  "audio.generate",
  "image.upscale",
  "video.upscale",
  "video.depth",
  "video.segment",
] as const;

export type NexraGenerationOperation = (typeof NEXRA_GENERATION_OPERATIONS)[number];

export const NEXRA_MEDIA_OPERATIONS = [
  "image.generate",
  "video.generate",
  "audio.generate",
  "image.upscale",
  "video.upscale",
  "video.depth",
  "video.segment",
] as const satisfies readonly NexraGenerationOperation[];

export type NexraMediaOperation = (typeof NEXRA_MEDIA_OPERATIONS)[number];

export const NEXRA_PUBLISHED_MEDIA_OPERATIONS = [
  "image.generate",
  "video.generate",
  "audio.generate",
  "image.upscale",
  "video.upscale",
  "video.depth",
  "video.segment",
] as const satisfies readonly NexraMediaOperation[];

export type NexraPublishedMediaOperation = (typeof NEXRA_PUBLISHED_MEDIA_OPERATIONS)[number];

export const NEXRA_MEDIA_FEATURES = [
  "text-to-image",
  "image-to-image",
  "image-reference",
  "image-edit",
  "image-inpaint",
  "image-control",
  "text-to-video",
  "first-frame",
  "first-last-frame",
  "image-to-video",
  "video-to-video",
  "audio-to-video",
  "avatar-video",
  "reference-image",
  "reference-video",
  "reference-audio",
  "omni-reference",
  "tts",
  "music",
  "sfx",
  "voice-reference",
  "voice-design",
  "scale-factor",
  "target-resolution",
  "video-depth",
  "video-segment",
] as const;

export type NexraMediaFeature = (typeof NEXRA_MEDIA_FEATURES)[number];

/** JSON wire locator. SDK-only Blob/File support is normalized into one of these forms. */
export type NexraMediaSource = string | AssetReference;

export type NexraTextRole = "prompt" | "negative_prompt" | "script" | "lyrics";
export type NexraImageRole = "source" | "reference" | "first_frame" | "middle_frame" | "last_frame" | "mask" | "control" | "subject" | "background";
export type NexraVideoRole = "source" | "reference" | "edit" | "extend";
export type NexraAudioRole = "source" | "reference" | "voice_reference";

export type NexraTextContentPart = Readonly<{
  type: "text";
  text: string;
  role?: NexraTextRole;
}>;

export type NexraImageContentPart = Readonly<{
  type: "image";
  source: NexraMediaSource;
  role?: NexraImageRole;
  /** Stable prompt reference name for models that address media as `@name`. */
  name?: string;
}>;

/** An image pinned inside a generated video; only models declaring this role accept it. */
export type NexraVideoKeyframeContentPart = Readonly<{
  type: "image";
  source: NexraMediaSource;
  role: "keyframe";
  /** Position in seconds, strictly inside the generated clip. */
  timestamp: number;
}>;

export type NexraVideoContentPart = Readonly<{
  type: "video";
  source: NexraMediaSource;
  role?: NexraVideoRole;
  name?: string;
}>;

export type NexraAudioContentPart = Readonly<{
  type: "audio";
  source: NexraMediaSource;
  role?: NexraAudioRole;
}>;

export type NexraResourceContentPart = Readonly<{
  type: "resource";
  resource: "element" | "voice";
  source: string;
  name: string;
}>;

export type NexraMediaContentPart =
  | NexraResourceContentPart
  | NexraTextContentPart
  | NexraImageContentPart
  | NexraVideoContentPart
  | NexraAudioContentPart;

export type NexraDraftContentPart = Readonly<{
  type: "draft_task";
  draft_task: Readonly<{ id: string }>;
}>;

export type NexraImageGenerateInput = Readonly<{
  content: readonly (NexraTextContentPart | NexraImageContentPart)[];
  output_mode?: "image" | "layers";
  prompt_optimization?: "standard" | "fast";
  aspect_ratio?: string;
  resolution?: string;
  quality?: string;
  count?: number;
  output_format?: string;
  seed?: number;
}>;

export type NexraImageLayer = Readonly<{
  image_index: number;
  z_index: number;
  size: string;
  name?: string;
  description?: string;
  output_format?: string;
  bounding_box?: Readonly<{
    absolute: readonly [number, number, number, number];
    normalized: readonly [number, number, number, number];
  }>;
}>;

export type NexraImageLayerDecompositionOutput = Readonly<{
  images: readonly string[];
  layer_decomposition: Readonly<{ layers: readonly NexraImageLayer[] }>;
}>;

export type NexraVideoGenerateInput = Readonly<{
  content: readonly (NexraMediaContentPart | NexraDraftContentPart | NexraVideoKeyframeContentPart)[];
  task_type?: string;
  draft?: boolean;
  /** Explicit no-watermark value, only on models whose contract declares it. */
  watermark?: false;
  /** Optional preset or cloned voice used by talking-avatar video models. */
  voice?: string;
  aspect_ratio?: string;
  resolution?: string;
  quality?: string;
  duration?: number;
  fit?: "contain" | "cover";
  title?: string;
  generate_audio?: boolean;
  output_format?: string;
  seed?: number;
}>;

export type NexraAudioGenerateInput = Readonly<{
  content: readonly (NexraTextContentPart | NexraAudioContentPart)[];
  voice?: string;
  language?: string;
  duration?: number;
  output_format?: string;
  sample_rate?: number;
  speed?: number;
  volume?: number;
  pitch?: number;
  emotion?: string;
  instrumental?: boolean;
  loop?: boolean;
  prompt_influence?: number;
  seed?: number;
}>;

export type NexraImageUpscaleInput = Readonly<{
  content: readonly [NexraImageContentPart];
  enable_hdr?: boolean;
  enable_wb?: boolean;
  scale?: number;
  resolution?: string;
  output_format?: string;
  preserve_details?: boolean;
}>;

export type NexraVideoUpscaleInput = Readonly<{
  source_duration: number;
  config?: string;
  scale?: number;
  resolution?: string;
  output_format?: string;
  preserve_details?: boolean;
}> & (Readonly<{ content: readonly [NexraVideoContentPart]; vid?: never }> | Readonly<{ vid: string; content?: never }>);

export type NexraVideoDepthInput = Readonly<{
  content: readonly [NexraVideoContentPart];
  resolution?: string;
}>;

export type NexraVideoSegmentInput = Readonly<{
  content: readonly [NexraVideoContentPart, NexraTextContentPart] | readonly [NexraTextContentPart, NexraVideoContentPart];
  resolution?: string;
}>;

export type NexraMediaInputByOperation = Readonly<{
  "image.generate": NexraImageGenerateInput;
  "video.generate": NexraVideoGenerateInput;
  "audio.generate": NexraAudioGenerateInput;
  "image.upscale": NexraImageUpscaleInput;
  "video.upscale": NexraVideoUpscaleInput;
  "video.depth": NexraVideoDepthInput;
  "video.segment": NexraVideoSegmentInput;
}>;

export type NexraMediaKind = "image" | "video" | "audio";

export type NexraMediaInputSlot = Readonly<{
  mediaKind: NexraMediaKind;
  role: string;
  min?: number;
  max: number;
  maxBytes?: number;
  minDurationSeconds?: number;
  maxDurationSeconds?: number;
}>;

export type NexraMediaInputMode = Readonly<{
  id: string;
  slots: readonly NexraMediaInputSlot[];
}>;

export type NexraMediaParameterValue = string | number | boolean;

export type NexraMediaParameter = Readonly<{
  key: string;
  label?: string;
}> & (
  | Readonly<{ kind: "enum"; options: readonly NexraMediaParameterValue[]; default: NexraMediaParameterValue; allowCustom?: boolean }>
  | Readonly<{ kind: "range"; min: number; max: number; step: number; default: number; autoValue?: number; unit?: string }>
  | Readonly<{ kind: "toggle"; default: boolean }>
  | Readonly<{ kind: "resource"; resource: "voice"; default?: string; allowCustom?: boolean }>
);

export type NexraMediaParameterConstraint = Readonly<{
  when: Readonly<Record<string, NexraMediaParameterValue | readonly NexraMediaParameterValue[]>>;
  parameter: string;
  allowedValues?: readonly NexraMediaParameterValue[];
  fixedValue?: NexraMediaParameterValue;
  disabled?: boolean;
}>;

export type NexraMediaOperationDefinition = Readonly<{
  operation: NexraMediaOperation;
  status: "active" | "hold";
  outputKind: NexraMediaKind;
  features: readonly NexraMediaFeature[];
  parameterKeys: readonly string[];
}>;

export const NEXRA_MEDIA_OPERATION_DEFINITIONS = Object.freeze([
  {
    operation: "image.generate",
    status: "active",
    outputKind: "image",
    features: ["text-to-image", "image-to-image", "image-reference", "image-edit", "image-inpaint", "image-control"],
    parameterKeys: ["aspect_ratio", "resolution", "quality", "count", "output_format", "seed", "output_mode", "prompt_optimization"],
  },
  {
    operation: "video.generate",
    status: "active",
    outputKind: "video",
    features: ["text-to-video", "first-frame", "first-last-frame", "image-to-video", "video-to-video", "audio-to-video", "avatar-video", "reference-image", "reference-video", "reference-audio", "omni-reference"],
    parameterKeys: ["voice", "aspect_ratio", "resolution", "quality", "duration", "fit", "title", "generate_audio", "output_format", "seed", "watermark"],
  },
  {
    operation: "audio.generate",
    status: "active",
    outputKind: "audio",
    features: ["tts", "music", "sfx", "voice-reference", "voice-design"],
    parameterKeys: ["voice", "language", "duration", "output_format", "sample_rate", "speed", "volume", "pitch", "emotion", "instrumental", "loop", "prompt_influence", "seed"],
  },
  {
    operation: "image.upscale",
    status: "active",
    outputKind: "image",
    features: ["scale-factor", "target-resolution"],
    parameterKeys: ["scale", "resolution", "output_format", "preserve_details"],
  },
  {
    operation: "video.upscale",
    status: "active",
    outputKind: "video",
    features: ["scale-factor", "target-resolution"],
    parameterKeys: ["scale", "resolution", "output_format", "preserve_details"],
  },
  {
    operation: "video.depth",
    status: "active",
    outputKind: "video",
    features: ["video-depth"],
    parameterKeys: ["resolution"],
  },
  {
    operation: "video.segment",
    status: "active",
    outputKind: "video",
    features: ["video-segment"],
    parameterKeys: ["resolution"],
  },
] as const satisfies readonly NexraMediaOperationDefinition[]);

const mediaSourceSchema: JsonObject = {
  oneOf: [
    { type: "string", minLength: 1 },
    {
      type: "object",
      properties: { assetId: { type: "string", minLength: 1 } },
      required: ["assetId"],
      additionalProperties: false,
    },
  ],
};

function textPartSchema(roles: readonly NexraTextRole[]): JsonObject {
  return {
    type: "object",
    properties: {
      type: { const: "text" },
      text: { type: "string", minLength: 1 },
      role: { type: "string", enum: [...roles] },
    },
    required: ["type", "text"],
    additionalProperties: false,
  };
}

function mediaPartSchema(
  type: NexraMediaKind,
  roles: readonly string[],
): JsonObject {
  return {
    type: "object",
    properties: {
      type: { const: type },
      source: structuredClone(mediaSourceSchema),
      role: { type: "string", enum: [...roles] },
      ...(type === "image" ? { name: { type: "string", minLength: 1, maxLength: 64, pattern: "^[^\\s@]+$" } } : {}),
    },
    required: ["type", "source"],
    additionalProperties: false,
  };
}

function inputSchema(
  contentItems: readonly JsonObject[],
  properties: JsonObject,
  contentBounds: Readonly<{ minItems?: number; maxItems?: number }> = {},
): JsonObject {
  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    type: "object",
    properties: {
      content: {
        type: "array",
        minItems: contentBounds.minItems ?? 1,
        ...(contentBounds.maxItems === undefined ? {} : { maxItems: contentBounds.maxItems }),
        items: { oneOf: contentItems.map((item) => structuredClone(item)) },
        "x-ui": { control: "content-parts", order: 0 },
      },
      ...properties,
    },
    required: ["content"],
    additionalProperties: false,
  };
}

const imagePart = mediaPartSchema("image", ["source", "reference", "first_frame", "last_frame", "mask", "control", "subject", "background"]);
const videoImagePart = mediaPartSchema("image", ["source", "reference", "first_frame", "middle_frame", "last_frame", "mask", "control", "subject", "background", "keyframe"]);
const videoPart = mediaPartSchema("video", ["source", "reference", "edit", "extend"]);
const audioPart = mediaPartSchema("audio", ["source", "reference", "voice_reference"]);
const resourcePart: JsonObject = {
  type: "object",
  properties: { type: { const: "resource" }, resource: { type: "string", enum: ["element", "voice"] },
    source: { type: "string", minLength: 1 }, name: { type: "string", minLength: 1 } },
  required: ["type", "resource", "source", "name"], additionalProperties: false,
};
const draftPart: JsonObject = {
  type: "object",
  properties: {
    type: { const: "draft_task" },
    draft_task: { type: "object", properties: { id: { type: "string", minLength: 1 } }, required: ["id"], additionalProperties: false },
  },
  required: ["type", "draft_task"], additionalProperties: false,
};

export const NEXRA_MEDIA_INPUT_SCHEMAS: Readonly<Record<NexraPublishedMediaOperation, JsonObject>> = Object.freeze({
  "image.generate": inputSchema(
    [textPartSchema(["prompt", "negative_prompt"]), imagePart],
    {
      output_mode: { type: "string", enum: ["image", "layers"], "x-ui": { hidden: true } },
      prompt_optimization: { type: "string", enum: ["standard", "fast"], "x-ui": { hidden: true } },
      aspect_ratio: { type: "string", minLength: 1, "x-ui": { order: 10 } },
      resolution: { type: "string", minLength: 1, "x-ui": { order: 11 } },
      quality: { type: "string", minLength: 1, "x-ui": { order: 12 } },
      count: { type: "integer", minimum: 1, "x-ui": { order: 13, control: "number" } },
      output_format: { type: "string", minLength: 1, "x-ui": { order: 14 } },
      seed: { type: "integer", minimum: 0, "x-ui": { order: 15, control: "number" } },
    },
  ),
  "video.generate": inputSchema(
    [textPartSchema(["prompt", "negative_prompt", "script"]), videoImagePart, videoPart, audioPart, draftPart, resourcePart],
    {
      task_type: { type: "string", minLength: 1, "x-ui": { order: 8, control: "segmented" } },
      voice: { type: "string", minLength: 1, "x-ui": { order: 9, control: "resource", resource: "voice" } },
      draft: { type: "boolean", "x-ui": { control: "switch" } },
      watermark: { type: "boolean", const: false, "x-ui": { hidden: true } },
      aspect_ratio: { type: "string", minLength: 1, "x-ui": { order: 10 } },
      resolution: { type: "string", minLength: 1, "x-ui": { order: 11 } },
      quality: { type: "string", minLength: 1, "x-ui": { order: 12 } },
      duration: { type: "number", exclusiveMinimum: 0, "x-ui": { order: 13, control: "number" } },
      fit: { type: "string", enum: ["contain", "cover"], "x-ui": { order: 15, control: "segmented" } },
      title: { type: "string", minLength: 1, "x-ui": { order: 16 } },
      generate_audio: { type: "boolean", "x-ui": { order: 14, control: "switch" } },
      output_format: { type: "string", minLength: 1, "x-ui": { order: 15 } },
      seed: { type: "integer", minimum: 0, "x-ui": { order: 16, control: "number" } },
    },
  ),
  "audio.generate": inputSchema(
    [textPartSchema(["prompt", "script", "lyrics"]), audioPart],
    {
      voice: { type: "string", minLength: 1, "x-ui": { order: 10, control: "resource", resource: "voice" } },
      language: { type: "string", minLength: 1, "x-ui": { order: 11 } },
      duration: { type: "number", exclusiveMinimum: 0, "x-ui": { order: 12, control: "number" } },
      output_format: { type: "string", minLength: 1, "x-ui": { order: 13 } },
      sample_rate: { type: "integer", minimum: 1, "x-ui": { order: 14, control: "number" } },
      speed: { type: "number", exclusiveMinimum: 0, "x-ui": { order: 15, control: "number" } },
      volume: { type: "number", "x-ui": { order: 16, control: "number" } },
      pitch: { type: "number", "x-ui": { order: 17, control: "number" } },
      emotion: { type: "string", minLength: 1, "x-ui": { order: 18 } },
      instrumental: { type: "boolean", "x-ui": { order: 19, control: "switch" } },
      loop: { type: "boolean", "x-ui": { order: 20, control: "switch" } },
      prompt_influence: { type: "number", minimum: 0, maximum: 1, "x-ui": { order: 21, control: "number" } },
      seed: { type: "integer", minimum: 0, "x-ui": { order: 22, control: "number" } },
    },
  ),
  "image.upscale": inputSchema(
    [mediaPartSchema("image", ["source"])],
    {
      scale: { type: "number", exclusiveMinimum: 1, "x-ui": { order: 10, control: "number" } },
      resolution: { type: "string", minLength: 1, "x-ui": { order: 11 } },
      output_format: { type: "string", minLength: 1, "x-ui": { order: 12 } },
      preserve_details: { type: "boolean", "x-ui": { order: 13, control: "switch" } },
    },
    { minItems: 1, maxItems: 1 },
  ),
  "video.upscale": inputSchema(
    [mediaPartSchema("video", ["source"])],
    {
      source_duration: { type: "number", exclusiveMinimum: 0, "x-ui": { order: 9, control: "number" } },
      scale: { type: "number", exclusiveMinimum: 1, "x-ui": { order: 10, control: "number" } },
      resolution: { type: "string", minLength: 1, "x-ui": { order: 11 } },
      output_format: { type: "string", minLength: 1, "x-ui": { order: 12 } },
      preserve_details: { type: "boolean", "x-ui": { order: 13, control: "switch" } },
    },
    { minItems: 1, maxItems: 1 },
  ),
  "video.segment": inputSchema(
    [textPartSchema(["prompt"]), mediaPartSchema("video", ["source"])],
    { resolution: { type: "string", minLength: 1, "x-ui": { order: 10 } } },
    { minItems: 2, maxItems: 2 },
  ),
  "video.depth": inputSchema(
    [mediaPartSchema("video", ["source"])],
    { resolution: { type: "string", minLength: 1, "x-ui": { order: 10 } } },
    { minItems: 1, maxItems: 1 },
  ),
});

export function nexraMediaInputSchema(operation: NexraMediaOperation): JsonObject | undefined {
  if (!(operation in NEXRA_MEDIA_INPUT_SCHEMAS)) return undefined;
  return structuredClone(NEXRA_MEDIA_INPUT_SCHEMAS[operation as NexraPublishedMediaOperation]);
}

export function isNexraMediaInput(value: unknown): value is JsonObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const input = value as JsonObject;
  // Native media bodies frequently use their own `content` array. The Nexra
  // The Task request reserves the top-level `model` field. Category parameters
  // therefore never contain a second model ID; one here identifies a Native body.
  if ("model" in input) return false;
  const content = input.content;
  return Array.isArray(content) && content.length > 0 && content.every((part) => {
    if (!part || typeof part !== "object" || Array.isArray(part)) return false;
    if (part.type === "text") return typeof part.text === "string" && part.text.length > 0;
    if (part.type === "resource") return ["element", "voice"].includes(String(part.resource))
      && typeof part.source === "string" && part.source.length > 0
      && typeof part.name === "string" && part.name.length > 0;
    if (part.type === "draft_task") {
      const reference = part.draft_task;
      return Boolean(reference && typeof reference === "object" && !Array.isArray(reference)
        && typeof reference.id === "string" && reference.id.length > 0);
    }
    if (!["image", "video", "audio"].includes(String(part.type))) return false;
    if (typeof part.source === "string") return part.source.length > 0;
    return Boolean(part.source && typeof part.source === "object" && !Array.isArray(part.source)
      && typeof part.source.assetId === "string" && part.source.assetId.length > 0);
  });
}

export function nexraMediaOperationForMode(mode: string): NexraPublishedMediaOperation | undefined {
  if (["text-to-image", "image-to-image"].includes(mode)) return "image.generate";
  if (["text-to-video", "image-to-video", "multimodal-to-video", "avatar-video"].includes(mode)) return "video.generate";
  if (["text-to-speech", "text-to-music", "text-to-sfx"].includes(mode)) return "audio.generate";
  if (mode === "image-upscale") return "image.upscale";
  if (mode === "video-upscale") return "video.upscale";
  if (mode === "video-depth") return "video.depth";
  if (mode === "video-segment") return "video.segment";
  return undefined;
}

/** Narrow the shared category vocabulary for one Experience Center input mode. */
export function nexraMediaInputSchemaForMode(mode: string): JsonObject | undefined {
  const operation = nexraMediaOperationForMode(mode);
  const schema = operation ? nexraMediaInputSchema(operation) : undefined;
  if (!schema) return undefined;
  const properties = schema.properties as JsonObject;
  const content = properties.content as JsonObject;
  const items = content.items as JsonObject;
  const variants = Array.isArray(items.oneOf) ? items.oneOf as JsonObject[] : [];
  if (mode === "avatar-video") {
    for (const variant of variants) {
      const variantProperties = variant.properties as JsonObject;
      if ((variantProperties.type as JsonObject).const === "text") {
        variantProperties.role = { type: "string", enum: ["script"] };
      }
    }
  }
  const allowed = mode === "text-to-image" || mode === "text-to-video" || mode === "text-to-music" || mode === "text-to-sfx"
    ? new Set(["text"])
    : mode === "image-to-image"
      ? new Set(["text", "image"])
      : mode === "image-to-video"
        ? new Set(["text", "image", "audio"])
      : mode === "text-to-speech"
        ? new Set(["text", "audio"])
        : new Set(["text", "image", "video", "audio"]);
  items.oneOf = variants.filter((variant) => allowed.has(String(((variant.properties as JsonObject).type as JsonObject).const)));
  if (mode === "image-to-video") {
    for (const variant of items.oneOf as JsonObject[]) {
      const variantProperties = variant.properties as JsonObject;
      if ((variantProperties.type as JsonObject).const !== "image") continue;
      variantProperties.role = { type: "string", enum: ["first_frame", "last_frame"] };
      const required = new Set(Array.isArray(variant.required) ? variant.required.filter((key): key is string => typeof key === "string") : []);
      required.add("role");
      variant.required = [...required];
    }
  }
  if (mode === "multimodal-to-video") {
    for (const variant of items.oneOf as JsonObject[]) {
      const variantProperties = variant.properties as JsonObject;
      const type = String((variantProperties.type as JsonObject).const);
      if (!["image", "video", "audio"].includes(type)) continue;
      variantProperties.role = type === "image"
        ? { type: "string", enum: ["reference", "subject", "background"] }
        : type === "video"
          ? { type: "string", enum: ["reference", "edit", "extend"] }
          : { type: "string", const: "reference" };
      const required = new Set(Array.isArray(variant.required) ? variant.required.filter((key): key is string => typeof key === "string") : []);
      required.add("role");
      variant.required = [...required];
    }
  }
  const requiredMedia = ["video-depth", "video-segment"].includes(mode) ? "video" : mode === "image-to-image" || mode === "image-to-video" || mode === "avatar-video" ? "image" : undefined;
  const requiresReferenceMedia = mode === "multimodal-to-video";
  const hasTextPart = (items.oneOf as JsonObject[]).some(variant => ((variant.properties as JsonObject).type as JsonObject).const === "text");
  content.allOf = [
    ...(mode === "avatar-video" || !hasTextPart ? [] : [{
      contains: { type: "object", properties: { type: { const: "text" } }, required: ["type"] },
      minContains: 1,
    }]),
    ...(requiredMedia ? [{
      contains: { type: "object", properties: { type: { const: requiredMedia } }, required: ["type"] },
      minContains: 1,
    }] : []),
    ...(requiresReferenceMedia ? [{
      contains: { type: "object", properties: { type: { enum: ["image", "video", "audio"] } }, required: ["type"] },
      minContains: 1,
    }] : []),
  ];
  if (mode === "text-to-speech") {
    properties.voice = { ...(properties.voice as JsonObject), "x-ui": { order: 10, control: "resource", resource: "voice" } };
  }
  const allowedParameters = mode === "avatar-video"
    ? new Set(["content", "voice", "aspect_ratio", "resolution"])
    : mode === "text-to-speech"
    ? new Set(["content", "voice", "language", "output_format", "sample_rate", "speed", "volume", "pitch", "emotion"])
    : mode === "text-to-music"
      ? new Set(["content", "duration", "output_format", "instrumental", "prompt_influence", "seed"])
      : mode === "text-to-sfx"
        ? new Set(["content", "duration", "output_format", "loop", "prompt_influence", "seed"])
        : undefined;
  if (allowedParameters) {
    schema.properties = Object.fromEntries(Object.entries(properties).filter(([key]) => allowedParameters.has(key)));
  }
  return schema;
}
